import React, { useCallback, useEffect, useState } from 'react';
import {
  Banknote, CalendarClock, Loader2, PackageX, Percent, Scale, Truck,
} from 'lucide-react';
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Badge } from '../../../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/select';
import { adminService } from '../../../../services/adminService';
import { toast } from 'sonner';
import { BANDS, RECOMMENDATIONS, money, pct, units } from './metrics';

const Metric = ({ label, value, hint, accent, testId }) => (
  <div className="rounded-lg border border-gray-200 p-3">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-lg font-bold mt-1 ${accent || 'text-gray-900'}`} data-testid={testId}>
      {value}
    </p>
    {hint && <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">{hint}</p>}
  </div>
);

export const ScorecardView = ({ params, clients, clientId, setClientId }) => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) { setCard(null); return; }
    setLoading(true);
    try {
      setCard(await adminService.getClientScorecard(clientId, params));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load the scorecard');
      setCard(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, params]);

  useEffect(() => { load(); }, [load]);

  const picker = (
    <Card>
      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Client</p>
          <Select value={clientId || ''} onValueChange={setClientId}>
            <SelectTrigger className="h-10" data-testid="scorecard-client-picker">
              <SelectValue placeholder="Pick a client to score" />
            </SelectTrigger>
            <SelectContent>
              {(clients || []).map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}{c.has_guaranteed_returns ? ' · GR' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-5">
        {picker}
        <div className="py-16 text-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" /></div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="space-y-5" data-testid="scorecard-view-empty">
        {picker}
        <Card className="border-dashed">
          <CardContent className="p-10 text-center text-gray-500 text-sm">
            Pick a client to build their scorecard — the artefact for a terms decision.
          </CardContent>
        </Card>
      </div>
    );
  }

  const m = card.metrics || {};
  const band = BANDS[m.status_band] || BANDS.healthy;
  const rec = RECOMMENDATIONS[card.recommendation?.action] || RECOMMENDATIONS.insufficient_data;
  const trad = card.if_traditional || {};
  const pay = card.payment_behaviour || {};
  const rel = card.delivery_reliability || {};

  return (
    <div className="space-y-5" data-testid="scorecard-view">
      {picker}

      <Card>
        <CardContent className="p-5 flex flex-col lg:flex-row gap-4 lg:items-center">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-xl font-bold text-gray-900" data-testid="scorecard-company">
                {card.company_name}
              </h3>
              <Badge variant="outline" className={band.chip} data-testid="scorecard-band">
                {band.label}
              </Badge>
              <Badge
                variant="outline"
                className={card.has_guaranteed_returns ? 'text-emerald-700 border-emerald-200' : 'text-gray-600'}
              >
                {card.client_type_name || (card.has_guaranteed_returns ? 'Guaranteed Returns' : 'Traditional')}
              </Badge>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {card.period?.start} → {card.period?.end}
              {card.effective_discount_pct !== null && card.effective_discount_pct !== undefined && (
                <> · charged {pct(card.effective_discount_pct)} below list wholesale</>
              )}
            </p>
          </div>
          <div className="lg:text-right">
            <Badge className={`${rec.chip} text-sm`} data-testid="scorecard-recommendation">
              {card.recommendation?.label || rec.label}
            </Badge>
            <ul className="mt-2 space-y-0.5 text-xs text-gray-600 lg:text-right">
              {(card.recommendation?.reasons || []).map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="scorecard-metrics">
        <Metric label="Sell-through" value={pct(m.sell_through_pct)} accent="text-emerald-700" hint={`${units(m.qty_sold)} of ${units(m.qty_delivered)} delivered`} testId="sc-sell-through" />
        <Metric label="Return rate" value={pct(m.return_rate_pct)} hint={`${units(m.qty_returned)} units back`} testId="sc-return-rate" />
        <Metric label="Over-order rate" value={pct(m.over_order_rate_pct)} hint={`${units(m.qty_ordered)} ordered`} />
        <Metric label="Fulfilment variance" value={pct(m.fulfilment_variance_pct)} hint="Delivered vs ordered — bakery side" />
        <Metric label="Gross billed" value={money(m.gross_billed)} hint="Invoice as raised" />
        <Metric label="Credit given" value={money(m.credit_given)} accent="text-rose-600" hint="Revenue GR hands back" testId="sc-credit-given" />
        <Metric label="Other credits" value={money(m.other_credits)} hint="Non-quantity credits & write-offs" />
        <Metric label="Net revenue" value={money(m.net_revenue)} testId="sc-net-revenue" />
        <Metric label="Cost of goods delivered" value={money(m.cost_of_goods_delivered)} hint={m.cost_recorded ? null : 'Cost not recorded on every line'} />
        <Metric label="Waste cost" value={money(m.waste_cost)} accent="text-rose-600" hint="Returns + terminal client-caused failures" />
        <Metric label="Contribution margin" value={money(m.contribution_margin)} testId="sc-margin" />
        <Metric label="Margin %" value={pct(m.contribution_margin_pct)} hint={`${pct(card.thresholds?.min_contribution_margin_pct)} minimum`} />
        <Metric label="Revenue per unit made" value={money(m.revenue_per_unit_produced)} hint="Ranks small & efficient fairly" />
        <Metric label="Orders" value={units(m.order_count)} hint={`${units(m.delivery_days)} delivery days`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-2 border-gray-900" data-testid="if-traditional-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Scale className="h-4 w-4 text-gray-700" /> If this client were on traditional wholesale
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Net revenue</p>
                <p className="text-lg font-bold text-gray-900" data-testid="if-trad-revenue">
                  {money(trad.net_revenue)}
                </p>
                <p className="text-[11px] text-gray-500">vs {money(m.net_revenue)} today</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500">Margin</p>
                <p className="text-lg font-bold text-gray-900">
                  {money(trad.contribution_margin)} · {pct(trad.contribution_margin_pct)}
                </p>
                <p className="text-[11px] text-gray-500">vs {pct(m.contribution_margin_pct)} today</p>
              </div>
            </div>
            <div className="rounded-lg bg-rose-50 border border-rose-200 p-3">
              <p className="text-xs uppercase tracking-wide text-rose-700">
                What Guaranteed Returns costs here
              </p>
              <p className="text-2xl font-bold text-rose-700" data-testid="gr-cost">
                {money(trad.gr_cost)}
              </p>
            </div>
            <p className="text-[11px] text-gray-500 leading-relaxed">{trad.note}</p>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Banknote className="h-4 w-4 text-emerald-600" /> Payment behaviour
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 text-sm" data-testid="payment-behaviour">
              <div>
                <p className="text-xs text-gray-500">Average days to pay</p>
                <p className="font-semibold">{pay.avg_days_to_pay ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Paid late</p>
                <p className="font-semibold">{pay.paid_late ?? 0} of {pay.paid_invoices ?? 0} ({pct(pay.paid_late_pct)})</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Open overdue</p>
                <p className="font-semibold">{pay.open_overdue ?? 0}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Outstanding</p>
                <p className="font-semibold">{money(pay.outstanding)}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="h-4 w-4 text-sky-600" /> Delivery reliability
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm" data-testid="delivery-reliability">
              <p>
                <span className="font-semibold">{rel.failed_count ?? 0}</span> failed deliveries
                still unresolved · {rel.client_caused ?? 0} client-caused · {rel.bakery_caused ?? 0}{' '}
                bakery-caused
                {rel.unrecorded ? ` · ${rel.unrecorded} cause not recorded` : ''}
              </p>
              <p className="text-rose-600 flex items-center gap-1">
                <PackageX className="h-3.5 w-3.5" /> {money(rel.waste_cost)} charged as
                client-caused waste
              </p>
              {(rel.orders || []).slice(0, 4).map((o) => (
                <p key={o.order_id} className="text-xs text-gray-500">
                  {o.order_number} · {o.delivery_date} · {o.cause.replace('_', '-')}
                  {o.notes ? ` · ${o.notes}` : ''}
                </p>
              ))}
              <p className="text-[11px] text-gray-400">
                A re-delivered order counts as an ordinary delivery and adds no waste.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Percent className="h-4 w-4 text-amber-500" /> Trailing 12 months
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(card.sparkline || []).length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No history in the last 12 months.</p>
          ) : (
            <div className="h-48" data-testid="scorecard-sparkline">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={card.sparkline} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <ChartTooltip />
                  <Area type="monotone" dataKey="sell_through_pct" name="Sell-through %" stroke="#d97706" fill="#fef3c7" />
                  <Area type="monotone" dataKey="net_revenue" name="Net revenue" stroke="#0ea5e9" fill="#e0f2fe" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-gray-600" /> Month by month
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]" data-testid="scorecard-monthly-table">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="p-2 text-left">Month</th>
                  <th className="p-2 text-right">Delivered</th>
                  <th className="p-2 text-right">Sold</th>
                  <th className="p-2 text-right">Sell-through</th>
                  <th className="p-2 text-right">Net revenue</th>
                  <th className="p-2 text-right">Waste</th>
                  <th className="p-2 text-right">Margin</th>
                </tr>
              </thead>
              <tbody>
                {(card.monthly || []).length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-gray-500">Nothing in this period.</td></tr>
                )}
                {(card.monthly || []).map((r) => (
                  <tr key={r.month} className="border-t">
                    <td className="p-2 font-medium">{r.month}</td>
                    <td className="p-2 text-right">{units(r.qty_delivered)}</td>
                    <td className="p-2 text-right">{units(r.qty_sold)}</td>
                    <td className="p-2 text-right">{pct(r.sell_through_pct)}</td>
                    <td className="p-2 text-right">{money(r.net_revenue)}</td>
                    <td className="p-2 text-right">{money(r.waste_cost)}</td>
                    <td className="p-2 text-right">
                      {money(r.contribution_margin)} · {pct(r.contribution_margin_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScorecardView;
